<template>
  <b-modal
    v-if="driver"
    id="send-driver-notification-modal"
    :title="`Send Notification to ${driver.firstName} ${driver.lastName}`"
    hide-footer
    @hidden="modalHiddenHandler"
  >
    <validation-observer ref="createNotificationRef">
      <b-form>
        <b-form-group>
          <validation-provider
            #default="{ errors }"
            name="title"
            rules="required|max:65"
          >
            <b-form-input
              id="title"
              v-model="notificationDetails.title"
              :state="errors.length > 0 ? false : null"
              name="title"
              placeholder="Enter Title"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group>
          <validation-provider
            #default="{ errors }"
            name="description"
            rules="required|max:240"
          >
            <b-form-textarea
              id="description"
              v-model="notificationDetails.description"
              :state="errors.length > 0 ? false : null"
              name="description"
              placeholder="Enter description"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-checkbox
          v-model="notificationDetails.saveToDb"
          switch
          class="font-weight-bolder"
        >Display the notification in the rider/driver app</b-form-checkbox>
        <template v-if="notificationDetails.saveToDb">
          <validation-provider
            #default="{ errors }"
            name="Expiry Date"
            rules="required"
          >
            <div class="expiry-date">
              <label>Expiry Date</label>
              <b-form-datepicker
                v-model="currentExpiryDate"
                value-as-date
                :min="new Date()"
              />
            </div>
            <small class="text-danger">{{ errors[0] }}</small>

          </validation-provider>
          <validation-provider
            #default="{ errors }"
            name="Expiry Time"
            rules="required"
          >
            <div>
              <label>Expiry Time</label>
              <b-form-timepicker v-model="currentExpiryTime" />
            </div>
            <small class="text-danger">{{ errors[0] }}</small>
            <div
              v-if="!isTimePresent"
              class="text-danger"
            >
              <small>The time you selected is in the past.</small>
            </div>
          </validation-provider>
        </template>
        <b-button
          :disabled="submitting"
          class="mt-1"
          variant="primary"
          @click="submit"
        >
          <b-spinner
            v-if="submitting"
            small
          />
          <span v-else>Send Notification</span>
        </b-button>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormGroup, BModal, BFormInput, BForm, BFormTextarea, BFormCheckbox, BFormDatepicker, BFormTimepicker, BButton, BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ref, watch, computed } from '@vue/composition-api'
import { max } from '@/utils/validations'
import { errorNotification, successNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BModal, BFormGroup, BFormInput, BForm, BFormTextarea, BFormCheckbox, BFormDatepicker, BFormTimepicker, ValidationProvider, ValidationObserver, BButton, BSpinner,
  },
  props: {
    driver: {
      type: Object,
      default: undefined,
    },
  },
  setup(props, ctx) {
    const createNotificationRef = ref(null)
    const notificationDetails = ref({
      title: '',
      description: '',
      saveToDb: false,
      expiryDate: null,
    })
    const currentExpiryDate = ref(null)
    const currentExpiryTime = ref(null)
    const isTimePresent = ref(true)

    watch(() => notificationDetails.value.saveToDb, val => {
      if (!val) {
        currentExpiryDate.value = undefined
        currentExpiryTime.value = undefined
      }
    })
    watch([currentExpiryDate, currentExpiryTime], () => {
      if (currentExpiryDate.value && currentExpiryTime.value) {
        const year = currentExpiryDate.value.getFullYear()
        const month = currentExpiryDate.value.getMonth() + 1
        const day = currentExpiryDate.value.getDate()
        const temp = new Date(`${year}-${month}-${day} ${currentExpiryTime.value}`)
        if (temp.getTime() < new Date().getTime()) {
          isTimePresent.value = false
        } else {
          isTimePresent.value = true
        }
        notificationDetails.value.expiryDate = `${currentExpiryDate.value.getFullYear()}-${currentExpiryDate.value.getMonth() + 1}-${currentExpiryDate.value.getDate()} ${currentExpiryTime.value}`
      }
    })
    const submitting = ref(false)
    const submit = async () => {
      try {
        submitting.value = true
        const valid = await createNotificationRef.value.validate()
        if (!valid) return
        if (notificationDetails.value.saveToDb) {
          if (!isTimePresent.value) return
        }
        const loggedUser = computed(() => ctx.root.$store.getters['auth/getLoggedUser'])
        await ctx.root.$store.dispatch('notifications/pushIndividualNotification', {
          save_to_db: notificationDetails.value.saveToDb,
          ttl: notificationDetails.value.saveToDb ? `${new Date(`${notificationDetails.value.expiryDate}`).getTime()}` : undefined,
          title: notificationDetails.value.title,
          description: notificationDetails.value.description,
          id: props.driver.externalId,
          type: 'driver',
          phone_no: props.driver.phoneNumber,
        })
        await ctx.root.$store.dispatch('notes/addNote', {
          notableType: 'driver',
          notableId: props.driver.id,
          note: `${notificationDetails.value.title}, ${notificationDetails.value.description}`,
          addedBy: loggedUser.value.email,
          category: 'Penalty',
        })
        successNotification('Notification sent successfully')
        ctx.root.$bvModal.hide('send-driver-notification-modal')
      } catch (error) {
        console.log(error)
        errorNotification('Something went wrong..')
      } finally {
        submitting.value = false
      }
    }
    const modalHiddenHandler = () => {
      Object.keys(notificationDetails.value).forEach(k => {
        notificationDetails.value[k] = null
      })

      currentExpiryDate.value = null
      currentExpiryTime.value = null
      isTimePresent.value = true
    }
    return {
      required,
      max,
      modalHiddenHandler,
      notificationDetails,
      createNotificationRef,
      submit,
      submitting,
      currentExpiryDate,
      isTimePresent,
      currentExpiryTime,
    }
  },
}
</script>

<style>

</style>
